import { useContext, useState } from 'react';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import { Icon, IconButton, TextField } from '@atom/mui';
import { AddIcon, DeleteIcon } from '@atom/mui/icons';
import { Summary } from '@atom/types/work';

import CreateWorkModalContext from '../../CreateWorkModalContext';
import {
  DEFAULT_SUMMARY_TITLE,
  restoreSummaryTitleOnBlur,
} from '../../createWorkModalUtilities';

import classes from './workDetails.module.css';

const styles = {
  iconButton: {
    padding: 0,
  },
};

const SummarySection = () => {
  const { draft, updateDraftField } = useContext(CreateWorkModalContext);
  const { summary } = draft;
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [titleBeforeEdit, setTitleBeforeEdit] = useState('');

  const [rowKeys, setRowKeys] = useState<number[]>(() =>
    summary.map((_, index) => index),
  );

  const handleAdd = () => {
    setRowKeys(keys => [...keys, keys.length ? Math.max(...keys) + 1 : 0]);
    updateDraftField('summary', [
      ...summary,
      { title: DEFAULT_SUMMARY_TITLE, contents: '' },
    ]);
  };

  const handleChange = (index: number, changes: Partial<Summary>) =>
    updateDraftField(
      'summary',
      summary.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...changes } : item,
      ),
    );

  const handleTitleBlur = (index: number) =>
    updateDraftField(
      'summary',
      restoreSummaryTitleOnBlur(summary, index, titleBeforeEdit),
    );

  const handleDelete = () => {
    setRowKeys(keys =>
      keys.filter((_, itemIndex) => itemIndex !== deleteIndex),
    );
    updateDraftField(
      'summary',
      summary.filter((_, itemIndex) => itemIndex !== deleteIndex),
    );
    setDeleteIndex(null);
  };

  return (
    <>
      <div className={classes.sectionHeader}>
        <div>Summary</div>
        <IconButton
          onClick={handleAdd}
          style={styles.iconButton}
          data-cy="createWorkModalAddSummaryButton"
        >
          <Icon icon={AddIcon} />
        </IconButton>
      </div>
      <div className={classes.sectionContent}>
        {summary.length === 0 ? (
          <div className={classes.emptySummary}>There is no summary.</div>
        ) : (
          summary.map((item, index) => (
            <div
              key={rowKeys[index]}
              className={classes.summaryRow}
              data-cy="createWorkModalSummaryRow"
            >
              <div className={classes.summaryRowHeader}>
                <div className={classes.summaryTitle}>
                  <TextField
                    required
                    fullWidth
                    value={item.title}
                    onFocus={() => setTitleBeforeEdit(item.title)}
                    onChange={event =>
                      handleChange(index, { title: event.target.value })
                    }
                    onBlur={() => handleTitleBlur(index)}
                    data-cy="createWorkModalSummaryTitleInput"
                  />
                </div>
                <IconButton
                  onClick={() => setDeleteIndex(index)}
                  style={styles.iconButton}
                  data-cy="createWorkModalSummaryDeleteButton"
                >
                  <Icon icon={DeleteIcon} />
                </IconButton>
              </div>
              <TextField
                fullWidth
                multiline
                placeholder="Add Description"
                value={item.contents}
                onChange={event =>
                  handleChange(index, { contents: event.target.value })
                }
                data-cy="createWorkModalSummaryDescriptionInput"
              />
            </div>
          ))
        )}
      </div>
      <DeleteModal
        open={!R.isNil(deleteIndex)}
        onCancel={() => setDeleteIndex(null)}
        onConfirm={handleDelete}
        title="Delete Summary?"
        content={
          !R.isNil(deleteIndex) ? (
            <span>
              Are you sure you want to delete summary{' '}
              <strong>
                "{summary[deleteIndex]?.title?.trim() || DEFAULT_SUMMARY_TITLE}"
              </strong>
              ?
            </span>
          ) : (
            ''
          )
        }
      />
    </>
  );
};

export default SummarySection;
